import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import PrintRounded from "@mui/icons-material/PrintRounded";
import SendRounded from "@mui/icons-material/SendRounded";
import ZoomInRounded from "@mui/icons-material/ZoomInRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import TvRounded from "@mui/icons-material/TvRounded";
import LiveTvRounded from "@mui/icons-material/LiveTvRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import { LoadingButton } from "@mui/lab";
import { useNavigate, useLocation, useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { PDFDownloadLink } from "@react-pdf/renderer";

import { DefaultPage } from "../../../layouts/default-page";
import { StatusChip } from "../../../components/common/StatusChip";
import { Notify } from "../../../layouts/notify";
import TVReportPDF from "./tv-report-pdf";
import {
  useGetDeputyTVReportByIdQuery,
  useUpdateDeputyTVReportStatusMutation,
} from "../../../services/report.query";
import { User } from "../../../models/User";

export default function TVReportDetailsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  const handleBack = () => {
    const returnUrl = location.state?.returnUrl || sessionStorage.getItem("last_deputy_tv_url");
    if (returnUrl) {
      navigate(returnUrl);
    } else {
      navigate("/deputies/tv-reports");
    }
  };

  const reportId = params.id || searchParams.get("id");
  const lUser: User = JSON.parse(localStorage.getItem("user") || "{}");

  const [status, setStatus] = useState<string>("acknowledged");
  const [adminComment, setAdminComment] = useState<string>("");
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);

  const {
    data: report,
    isLoading,
    isError,
    refetch,
  } = useGetDeputyTVReportByIdQuery(reportId as string, {
    skip: !reportId,
  });

  const [updateStatus, { isLoading: updating }] =
    useUpdateDeputyTVReportStatusMutation();

  useEffect(() => {
    if (!report?.report_status) return;
    const s = String(report.report_status).toUpperCase();
    if (["ACKNOWLEDGED", "DUPLICATE", "REJECTED"].includes(s)) {
      setStatus(s.toLowerCase());
    } else {
      setStatus("acknowledged");
    }

    if (report.admin_comment) {
      setAdminComment(report.admin_comment);
    }
  }, [report]);

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setStatus(event.target.value);
  };

  const handleUpdateStatusAndRemarks = async () => {
    if (!report?.id) return;
    try {
      await updateStatus({
        id: String(report.id),
        status: status.toUpperCase() as "ACKNOWLEDGED" | "DUPLICATE" | "REJECTED",
        user_id: lUser.user_id,
        admin_comment: adminComment.trim(),
      }).unwrap();

      if (sendEmail && report?.user?.email) {
        try {
          const deputy = report.user;
          const payload = {
            recipient: deputy.email,
            deputyId: deputy.deputy_control_number ?? "",
            firstName: deputy.name ?? "",
            lastName: deputy.last_name ?? "",
            status: status.toUpperCase(),
            comment: adminComment.trim(),
            reportId: report.id,
            reportType: "Television",
            withViolation: !!report.has_violation,
            description: report.comments ?? null,
            dateTime: new Date(
              report.program_datetime ?? report.created_at
            ).toISOString(),
          };

          const resp = await fetch(
            "https://mtrcb-app-server.vercel.app/api/email/send/report-comment",
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            }
          );
          const json = await resp.json().catch(() => ({}));
          if (!resp.ok || json?.success !== true) {
            Notify("warning", "Status saved, but email failed to send.");
          } else {
            Notify("success", "Status updated & deputy notified via email.");
          }
        } catch {
          Notify("warning", "Status saved, but email failed to send.");
        }
      } else {
        Notify("success", "TV report status updated successfully.");
      }

      refetch();
    } catch (err: any) {
      console.error("Error updating status:", err);
      Notify("error", err?.message || "Failed to update report status.");
    }
  };

  if (isLoading && !report) {
    return (
      <DefaultPage title="Deputy TV Report Details" subTitle="Loading report details...">
        <Box sx={{ p: 3, width: "100%" }}>
          <Skeleton variant="rectangular" height={45} sx={{ borderRadius: "4px", mb: 2 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <Skeleton variant="rectangular" height={220} sx={{ borderRadius: "6px", mb: 2 }} />
              <Skeleton variant="rectangular" height={240} sx={{ borderRadius: "6px" }} />
            </Grid>
            <Grid item xs={12} lg={5}>
              <Skeleton variant="rectangular" height={360} sx={{ borderRadius: "6px" }} />
            </Grid>
          </Grid>
        </Box>
      </DefaultPage>
    );
  }

  if (!report) {
    return (
      <DefaultPage title="Deputy TV Report" subTitle="Report not found">
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography color="error" variant="h6" mb={2}>
            {isError ? "Failed to load TV monitoring report details." : "Report not found or was removed."}
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackRounded />}
            onClick={handleBack}
            sx={{ backgroundColor: "#0072BC", borderRadius: "4px", textTransform: "none" }}
          >
            Back to TV Reports
          </Button>
        </Box>
      </DefaultPage>
    );
  }

  const deputy = report.user || {};
  const attachments = report.attachments || [];
  const imageAttachments = attachments.filter(
    (a: any) => a.type === "IMAGE" || a.file_url?.match(/\.(jpeg|jpg|png|webp)/i)
  );
  const videoAttachments = attachments.filter(
    (a: any) => a.type === "VIDEO" || a.file_url?.match(/\.(mp4|mov|webm)/i)
  );

  return (
    <DefaultPage
      title="Deputy TV Report Details"
      subTitle="Review, evaluate, and moderate television broadcast monitoring reports submitted by MTRCB Deputies."
    >
      {/* Top Navigation & Action Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackRounded fontSize="small" />}
          onClick={handleBack}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "4px",
            borderColor: "#CBD5E1",
            color: "#475569",
            "&:hover": {
              borderColor: "#94A3B8",
              backgroundColor: "#F8FAFC",
            },
          }}
        >
          Back to TV Reports
        </Button>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <StatusChip label={report.report_status || "PENDING"} />
          <PDFDownloadLink
            document={<TVReportPDF report={report} />}
            fileName={`TV-Report-${report.id}.pdf`}
            style={{ textDecoration: "none" }}
          >
            {({ loading }) => (
              <Button
                variant="contained"
                size="small"
                startIcon={<PrintRounded fontSize="small" />}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontWeight: 650,
                  borderRadius: "4px",
                  backgroundColor: "#0072BC",
                  "&:hover": { backgroundColor: "#005a96" },
                  boxShadow: "none",
                }}
              >
                {loading ? "Preparing PDF..." : "Print PDF"}
              </Button>
            )}
          </PDFDownloadLink>
        </Stack>
      </Box>

      {/* Main Grid Content */}
      <Grid container spacing={3}>
        {/* Left Column: Details & Evidence */}
        <Grid item xs={12} lg={7}>
          <Stack spacing={3}>
            {/* Deputy Inspector Information Card */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#0072BC",
                    mb: 2,
                  }}
                >
                  Deputy Inspector Information
                </Typography>

                <Stack direction="row" spacing={2.5} alignItems="center" mb={2.5}>
                  <Avatar
                    src={deputy.profile_picture}
                    alt={deputy.name}
                    sx={{
                      width: 64,
                      height: 64,
                      border: "2px solid #E2E8F0",
                      backgroundColor: "#F1F5F9",
                      fontSize: 22,
                      fontWeight: 700,
                      color: "#0072BC",
                    }}
                  >
                    {deputy.name?.[0] || "D"}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#1E293B" }}>
                      {deputy.name} {deputy.last_name}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "#64748B", mt: 0.2 }}>
                      Deputy Inspector • {deputy.sub_role || "DEPUTY"}
                    </Typography>
                  </Box>
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Email Address
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1E293B", mt: 0.3 }}>
                      {deputy.email || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Contact Number
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1E293B", mt: 0.3 }}>
                      {deputy.phone_number || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Control Number
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1E293B", mt: 0.3 }}>
                      {deputy.deputy_control_number || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Report Reference ID
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0072BC", mt: 0.3 }}>
                      #{report.id}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={12}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Date & Time Reported
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1E293B", mt: 0.3 }}>
                      {report.created_at
                        ? format(new Date(report.created_at), "MMM dd, yyyy - hh:mm a")
                        : "N/A"}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* TV Program & Broadcast Details Card */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#0072BC",
                    mb: 2,
                  }}
                >
                  TV Program & Broadcast Details
                </Typography>

                <Grid container spacing={2} mb={2.5}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      TV Network / Station
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1E293B", mt: 0.3, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <TvRounded sx={{ fontSize: 18, color: "#0072BC" }} />
                      {report.tv_network || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      TV Program Title
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1E293B", mt: 0.3, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <LiveTvRounded sx={{ fontSize: 18, color: "#0072BC" }} />
                      {report.tv_program || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Classification Rating
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1E293B", mt: 0.3 }}>
                      {report.rating || "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Aired On (Date & Time)
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1E293B", mt: 0.3 }}>
                      {report.program_datetime
                        ? format(new Date(report.program_datetime), "MMM dd, yyyy - hh:mm a")
                        : "N/A"}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={12}>
                    <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Violation Status
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {report.has_violation ? (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.8,
                            px: 1.2,
                            py: 0.4,
                            borderRadius: "4px",
                            backgroundColor: "#FEF2F2",
                            border: "1px solid #FCA5A5",
                            color: "#DC2626",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          <WarningAmberRounded sx={{ fontSize: 16 }} />
                          With Violation
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.8,
                            px: 1.2,
                            py: 0.4,
                            borderRadius: "4px",
                            backgroundColor: "#F0FDF4",
                            border: "1px solid #86EFAC",
                            color: "#16A34A",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          <CheckCircleOutlineRounded sx={{ fontSize: 16 }} />
                          Without Violation
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2, borderColor: "#F1F5F9" }} />

                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    color: "#64748B",
                    mb: 1,
                  }}
                >
                  Deputy Observations & Comments
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: "4px",
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E2E8F0",
                    color: report.comments ? "#334155" : "#94A3B8",
                    fontSize: 13,
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {report.comments || "No comments or observations recorded."}
                </Box>
              </CardContent>
            </Card>

            {/* Media Attachments & Evidence Card */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#0072BC",
                    mb: 2,
                  }}
                >
                  Media Attachments & Evidence ({attachments.length})
                </Typography>

                {imageAttachments.length > 0 && (
                  <Box mb={videoAttachments.length > 0 ? 3 : 0}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#64748B", mb: 1.5 }}>
                      Photographic Evidence ({imageAttachments.length})
                    </Typography>
                    <Grid container spacing={2}>
                      {imageAttachments.map((img: any, idx: number) => (
                        <Grid item xs={6} sm={3} key={img.id || idx}>
                          <Box
                            sx={{
                              position: "relative",
                              borderRadius: "4px",
                              overflow: "hidden",
                              border: "1px solid #E2E8F0",
                              aspectRatio: "4/3",
                              cursor: "pointer",
                              "&:hover .zoom-overlay": { opacity: 1 },
                            }}
                            onClick={() => setZoomedImage(img.file_url)}
                          >
                            <CardMedia
                              component="img"
                              image={img.file_url}
                              alt={`Evidence photo ${idx + 1}`}
                              sx={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                            <Box
                              className="zoom-overlay"
                              sx={{
                                position: "absolute",
                                inset: 0,
                                backgroundColor: "rgba(0, 0, 0, 0.4)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                opacity: 0,
                                transition: "opacity 0.2s ease-in-out",
                                color: "#FFFFFF",
                              }}
                            >
                              <ZoomInRounded sx={{ fontSize: 28 }} />
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {videoAttachments.length > 0 && (
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#64748B", mb: 1.5 }}>
                      Video Recording Evidence
                    </Typography>
                    {videoAttachments.map((vid: any, idx: number) => (
                      <Box
                        key={vid.id || idx}
                        sx={{
                          borderRadius: "4px",
                          overflow: "hidden",
                          border: "1px solid #E2E8F0",
                          backgroundColor: "#000000",
                          maxWidth: 500,
                        }}
                      >
                        <video
                          controls
                          width="100%"
                          style={{ display: "block", maxHeight: 360 }}
                        >
                          <source src={vid.file_url} type="video/mp4" />
                          Your browser does not support HTML5 video streaming.
                        </video>
                      </Box>
                    ))}
                  </Box>
                )}

                {attachments.length === 0 && (
                  <Box
                    sx={{
                      p: 4,
                      borderRadius: "6px",
                      border: "1px dashed #CBD5E1",
                      backgroundColor: "#F8FAFC",
                      textAlign: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: 13, color: "#94A3B8" }}>
                      No photo or video attachments were uploaded with this report.
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Right Column: Resolution & Status */}
        <Grid item xs={12} lg={5}>
          <Stack spacing={3}>
            {/* Status & Resolution Card */}
            <Card
              elevation={0}
              sx={{
                border: "1px solid #E2E8F0",
                borderRadius: "6px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    color: "#0072BC",
                    mb: 2,
                  }}
                >
                  Resolution & Status
                </Typography>

                <Box mb={2.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="report-status-select-label">Change Report Status</InputLabel>
                    <Select
                      labelId="report-status-select-label"
                      value={status}
                      label="Change Report Status"
                      onChange={handleStatusChange}
                      sx={{ borderRadius: "4px" }}
                    >
                      <MenuItem value="acknowledged">ACKNOWLEDGED (Approved)</MenuItem>
                      <MenuItem value="duplicate">DUPLICATE</MenuItem>
                      <MenuItem value="rejected">REJECTED</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box mb={2.5}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#475569",
                      mb: 0.8,
                    }}
                  >
                    Official Administrative Remarks
                  </Typography>
                  <TextField
                    multiline
                    rows={4}
                    placeholder="Enter official resolution notes or comments to be communicated to the deputy..."
                    value={adminComment}
                    onChange={(e) => setAdminComment(e.target.value)}
                    size="small"
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "4px",
                        fontSize: 13,
                      },
                    }}
                  />
                  <Typography sx={{ fontSize: 11, color: "#94A3B8", mt: 0.5 }}>
                    These remarks will be saved into the database and viewable in audit records.
                  </Typography>
                </Box>

                <Box mb={2.5}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={sendEmail}
                        onChange={(e) => setSendEmail(e.target.checked)}
                        sx={{
                          color: "#94A3B8",
                          "&.Mui-checked": { color: "#0072BC" },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 12, color: "#475569", fontWeight: 500 }}>
                        Notify deputy via official email notification
                      </Typography>
                    }
                  />
                </Box>

                <LoadingButton
                  fullWidth
                  variant="contained"
                  loading={updating}
                  startIcon={<SendRounded />}
                  onClick={handleUpdateStatusAndRemarks}
                  sx={{
                    borderRadius: "4px",
                    backgroundColor: "#0072BC",
                    textTransform: "none",
                    fontWeight: 650,
                    py: 1.1,
                    fontSize: 13,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#005a96",
                      boxShadow: "none",
                    },
                  }}
                >
                  Update Status & Send Remarks
                </LoadingButton>
              </CardContent>
            </Card>

            {/* Audit / Evaluation History Card */}
            {(report.admin || report.status_updated_at || report.status_updated_by) && (
              <Card
                elevation={0}
                sx={{
                  border: "1px solid #E2E8F0",
                  borderRadius: "6px",
                  backgroundColor: "#FFFFFF",
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      color: "#64748B",
                      mb: 1.5,
                    }}
                  >
                    Last Evaluation Record
                  </Typography>

                  <Stack spacing={1}>
                    {report.admin && (
                      <Box>
                        <Typography sx={{ fontSize: 11, color: "#94A3B8", textTransform: "uppercase", fontWeight: 600 }}>
                          Evaluated By
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1E293B" }}>
                          {report.admin.name} {report.admin.last_name}
                        </Typography>
                      </Box>
                    )}

                    {report.status_updated_at && (
                      <Box>
                        <Typography sx={{ fontSize: 11, color: "#94A3B8", textTransform: "uppercase", fontWeight: 600 }}>
                          Timestamp
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#475569" }}>
                          {format(new Date(report.status_updated_at), "MMM dd, yyyy - hh:mm a")}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>

      {/* Zoomable Image Lightbox Dialog */}
      <Dialog
        open={Boolean(zoomedImage)}
        onClose={() => setZoomedImage(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "6px",
            overflow: "hidden",
            backgroundColor: "#000000",
          },
        }}
      >
        <Box sx={{ position: "relative", p: 1 }}>
          <IconButton
            onClick={() => setZoomedImage(null)}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#FFFFFF",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
              zIndex: 1,
            }}
          >
            <CloseRounded />
          </IconButton>
          {zoomedImage && (
            <CardMedia
              component="img"
              image={zoomedImage}
              alt="Zoomed evidence"
              sx={{
                width: "100%",
                maxHeight: "80vh",
                objectFit: "contain",
              }}
            />
          )}
        </Box>
      </Dialog>
    </DefaultPage>
  );
}
